<template>
  <v-card flat>
    <v-card-title class="d-flex align-center flex-wrap">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        label="Search Pending Payments"
        single-line
        hide-details
        outlined
        dense
        class="mr-4 mb-2 mb-sm-0"
        style="max-width: 300px"
        clearable
        @input="onSearch"
      ></v-text-field>
      <v-spacer />
      <v-btn-toggle v-model="displayType" mandatory borderless color="primary">
        <v-btn value="grid" icon>
          <v-icon>mdi-view-grid</v-icon>
        </v-btn>
        <v-btn value="list" icon>
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>
      </v-btn-toggle>
    </v-card-title>

    <v-card-text>
      <!-- Loading State -->

      <v-row
        v-if="loading && requests.length === 0"
        justify="center"
        class="py-10"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-row>

      <v-alert
        v-if="!loading && requests.length === 0"
        type="info"
        text
        class="mt-4"
      >
        You have no pending payment requests at the moment.
      </v-alert>

      <!-- Grid View -->

      <v-row v-if="displayType === 'grid' && requests.length > 0">
        <v-col
          v-for="item in requests"
          :key="item.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card
            hover
            outlined
            height="100%"
            class="d-flex flex-column transition-swing"
            @click="pay(item)"
          >
            <v-list-item three-line>
              <v-list-item-content>
                <div class="overline mb-2 primary--text font-weight-bold">
                  {{ item.no }}
                </div>

                <v-list-item-title class="headline mb-1 text-wrap">
                  {{ item.serviceDescription }}
                </v-list-item-title>

                <v-list-item-subtitle class="mt-2">
                  <v-chip x-small label color="grey lighten-3" class="mr-1">{{
                    item.serviceType
                  }}</v-chip>

                  <span class="caption grey--text">{{
                    formatDate(item.requestDate)
                  }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-card-text class="flex-grow-1 pt-0">
              <div class="display-1 primary--text font-weight-light">
                {{ item.amount | currency }}
              </div>

              <div
                class="caption mt-2"
                :class="
                  item.status === 'Released' ? 'success--text' : 'orange--text'
                "
              >
                <v-icon
                  x-small
                  :color="item.status === 'Released' ? 'success' : 'orange'"
                  >mdi-circle</v-icon
                >

                {{ item.status }}
              </div>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4 bg-grey-lighten-4">
              <v-btn color="primary" depressed @click.stop="pay(item)">
                Pay Now
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                icon
                color="grey darken-1"
                @click.stop="viewDetails(item)"
                title="View Details"
              >
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- List View -->

      <v-data-table
        v-else-if="displayType === 'list' && requests.length > 0"
        :headers="headers"
        :items="requests"
        :loading="loading"
        hide-default-footer
        class="elevation-0 border-top mt-4 clickable-rows"
        @click:row="pay"
      >
        <template v-slot:item.amount="{ item }">
          <span class="font-weight-bold primary--text">{{
            item.amount | currency
          }}</span>
        </template>

        <template v-slot:item.requestDate="{ item }">
          {{ formatDate(item.requestDate) }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            :color="item.status === 'Released' ? 'success' : 'orange'"
            outlined
          >
            {{ item.status }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center">
            <v-btn
              small
              color="primary"
              depressed
              class="mr-2"
              @click.stop="pay(item)"
            >
              Pay Now
            </v-btn>

            <v-btn
              small
              icon
              color="grey darken-1"
              @click.stop="viewDetails(item)"
            >
              <v-icon small>mdi-eye-outline</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Load More / Pagination -->

      <div v-if="requests.length > 0" class="text-center py-6">
        <v-btn
          v-if="requests.length < pagination.total"
          color="primary"
          outlined
          :loading="loading"
          @click="loadMore"
        >
          <v-icon left>mdi-chevron-down</v-icon>

          Show More Requests ({{ requests.length }} of {{ pagination.total }})
        </v-btn>

        <div v-else class="caption grey--text">
          Showing all {{ pagination.total }} requests
        </div>
      </div>

      <v-navigation-drawer
        v-model="detailsDrawer"
        fixed
        right
        temporary
        width="450"
        overlay-color="primary"
        overlay-opacity="0.1"
      >
        <v-card flat height="100%" class="d-flex flex-column">
          <v-toolbar flat color="primary" dark>
            <v-toolbar-title class="subtitle-1"
              >Request Details</v-toolbar-title
            >

            <v-spacer></v-spacer>

            <v-btn icon @click="detailsDrawer = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-toolbar>

          <v-card-text class="flex-grow-1 pa-0">
            <v-list two-line subheader>
              <v-subheader>General Information</v-subheader>

              <v-list-item>
                <v-list-item-avatar color="primary lighten-5">
                  <v-icon color="primary">mdi-file-document-outline</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-subtitle>Request Number</v-list-item-subtitle>

                  <v-list-item-title class="font-weight-bold">{{
                    selectedItem ? selectedItem.no : ""
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-avatar color="primary lighten-5">
                  <v-icon color="primary">mdi-cog-outline</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-subtitle
                    >Service Description</v-list-item-subtitle
                  >

                  <v-list-item-title>{{
                    selectedItem ? selectedItem.serviceDescription : ""
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-subheader>Financial Details</v-subheader>

              <v-list-item>
                <v-list-item-avatar color="success lighten-5">
                  <v-icon color="success">mdi-cash-multiple</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-subtitle>Amount Payable</v-list-item-subtitle>

                  <v-list-item-title
                    class="display-1 success--text font-weight-bold"
                  >
                    {{ selectedItem ? selectedItem.amount : 0 | currency }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item
                v-if="selectedItem && selectedItem.proformaInvoiceNo"
              >
                <v-list-item-avatar color="grey lighten-4">
                  <v-icon color="grey darken-2"
                    >mdi-receipt-text-outline</v-icon
                  >
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-subtitle>Proforma Invoice</v-list-item-subtitle>

                  <v-list-item-title>{{
                    selectedItem.proformaInvoiceNo
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-divider inset></v-divider>

              <v-subheader>Timeline & Status</v-subheader>

              <v-list-item>
                <v-list-item-avatar color="grey lighten-4">
                  <v-icon color="grey darken-2">mdi-calendar-clock</v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-subtitle>Date Requested</v-list-item-subtitle>

                  <v-list-item-title>{{
                    selectedItem ? formatDate(selectedItem.requestDate) : ""
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-avatar color="grey lighten-4">
                  <v-icon color="grey darken-2"
                    >mdi-check-circle-outline</v-icon
                  >
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-subtitle>Current Status</v-list-item-subtitle>

                  <v-list-item-title>
                    <v-chip
                      small
                      :color="
                        selectedItem && selectedItem.status === 'Released'
                          ? 'success'
                          : 'orange'
                      "
                      dark
                    >
                      {{ selectedItem ? selectedItem.status : "" }}
                    </v-chip>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-6" v-if="selectedItem">
            <v-btn
              color="primary"
              block
              x-large
              depressed
              @click="pay(selectedItem)"
            >
              Proceed to Secure Payment

              <v-icon right>mdi-lock-outline</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>

      <Checkout />
    </v-card-text>
  </v-card>
</template>

<script>
import DateMixin from "@/mixins/DateMixin";
import { appUrl } from "@/environment/environment";
import { AuthService } from "../../auth";
import Checkout from "@kinetics254/paygate/src/views/Checkout.vue";

export default {
  name: "PendingPayments",
  mixins: [DateMixin],
  components: { Checkout },
  data: () => ({
    displayType: "grid",
    detailsDrawer: false,
    selectedItem: null,
    search: "",
    searchTimeout: null,
    headers: [
      { text: "No", value: "no" },
      { text: "Service", value: "serviceDescription" },
      { text: "Date", value: "requestDate" },
      { text: "Amount", value: "amount" },
      { text: "Status", value: "status" },
      { text: "Actions", value: "actions", sortable: false },
    ],
  }),
  computed: {
    requests() {
      return this.$store.getters["Payments/pendingRequests"];
    },
    serviceTypes() {
      return this.$store.getters["Payments/serviceTypes"];
    },
    loading() {
      return this.$store.getters["Payments/loading"];
    },
    pagination() {
      return this.$store.getters["Payments/pagination"].pending;
    },
    page: {
      get() {
        return this.pagination.page;
      },
      set(val) {
        this.$store.commit("Payments/SET_PAGINATION", {
          type: "pending",
          data: { page: val },
        });
      },
    },
    pageCount() {
      return (
        Math.ceil(this.pagination.total / this.pagination.itemsPerPage) || 1
      );
    },
    profile() {
      return AuthService.user?.profile || {};
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    onSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.fetchData();
      }, 500);
    },
    fetchData() {
      const skip = (this.page - 1) * this.pagination.itemsPerPage;
      const top = this.pagination.itemsPerPage;
      const where = JSON.stringify({
        profileID: this.profile.ProfileID,
        status: "New",
        billed: true,
        documentType: "Invoice",
      });

      this.$store.dispatch("Payments/getPendingRequests", {
        $skip: skip,
        $top: top,
        $filter: where,
        search: this.search,
      });
    },
    onPageChange() {
      this.fetchData();
    },
    loadMore() {
      const nextPage = this.page + 1;
      this.page = nextPage;
      const skip = (nextPage - 1) * this.pagination.itemsPerPage;
      const top = this.pagination.itemsPerPage;

      this.$store.dispatch("Payments/loadMorePendingRequests", {
        ...(this.pagination.nextCursor
          ? { cursor: this.pagination.nextCursor }
          : {
              $skip: skip,
              $top: top,
              search: this.search,
            }),
      });
    },
    viewDetails(item) {
      this.selectedItem = item;
      this.detailsDrawer = true;
    },
    pay(data) {
      const PhoneNo =
        this.profile.PhoneNo.charAt(0) === "+"
          ? this.profile.PhoneNo.slice(1)
          : this.profile.PhoneNo.charAt(0) === "0"
          ? `254${this.profile.PhoneNo.slice(1)}`
          : this.profile.PhoneNo;

      const chargeType = this.serviceTypes?.find((item) => {
        return item.Charge_Item === data.serviceType;
      });

      if (!chargeType) {
        this.$store.commit("Payments/SET_ALERT", {
          status: "error",
          message: "Service request charge could not be found!",
        });
        return;
      }

      let payload = {
        payerID: data.profileID,
        sourceDocumentNo: data.no,
        serviceID: chargeType.Service_Request_Code,
        description: data.serviceDescription || "Service Request Payment",
        amount: data.amount,
        accountNo: PhoneNo,
        email: this.profile.Email,
        idNumber: this.profile.IdentificationDocumentNo,
        name: data.memberName,
        success_url: new URL(
          this.$router.resolve({ name: "Payments" }).href,
          appUrl
        ),
        fail_url: window.location.href,
      };

      payload = btoa(JSON.stringify(payload));
      this.$checkout.pay(payload);
    },
  },
};
</script>
